/**
 * Copyright © 2026 Adnuntius AS.
 *
 * Angular-native port of angular-object-diff's ObjectDiff factory.
 * Returns plain HTML strings (callers pipe through safeHtml).
 */
import {Injectable} from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class ObjectDiffService {
  private openChar = '{';
  private closeChar = '}';

  setOpenChar(char: string): void {
    this.openChar = char;
  }

  setCloseChar(char: string): void {
    this.closeChar = char;
  }

  diff(a: any, b: any, shallow?: boolean, isOwn?: boolean): any {
    if (a === b) {
      return this.equalObj(a);
    }

    const diffValue: any = {};
    let equal = true;

    for (const key in a) {
      if ((!isOwn && key in b) || (isOwn && typeof b !== 'undefined' && Object.prototype.hasOwnProperty.call(b, key))) {
        if (a[key] === b[key]) {
          diffValue[key] = this.equalObj(a[key]);
        } else if (!shallow && this.isValidAttr(a[key], b[key])) {
          const valueDiff = this.diff(a[key], b[key], shallow, isOwn);
          if (valueDiff.changed === 'equal') {
            diffValue[key] = this.equalObj(a[key]);
          } else {
            equal = false;
            diffValue[key] = valueDiff;
          }
        } else {
          equal = false;
          diffValue[key] = {
            changed: 'primitive change',
            removed: a[key],
            added: b[key]
          };
        }
      } else {
        equal = false;
        diffValue[key] = {
          changed: 'removed',
          value: a[key]
        };
      }
    }

    for (const key in b) {
      if ((!isOwn && !(key in a)) || (isOwn && typeof a !== 'undefined' && !Object.prototype.hasOwnProperty.call(a, key))) {
        equal = false;
        diffValue[key] = {
          changed: 'added',
          value: b[key]
        };
      }
    }

    if (equal) {
      return this.equalObj(a);
    }
    return {
      changed: 'object change',
      value: diffValue
    };
  }

  diffOwnProperties(a: any, b: any, shallow?: boolean): any {
    return this.diff(a, b, shallow, true);
  }

  toJsonView(changes: any, shallow?: boolean): string {
    const properties: string[] = [];
    const diff = changes.value;
    if (changes.changed === 'equal') {
      return this.inspect(diff, shallow);
    }

    for (const key in diff) {
      properties.push(this.formatChange(key, diff[key], shallow));
    }

    return '<span>' + this.openChar + '</span>\n<div class="diff-level">' + properties.join('<span>,</span>\n') +
      '\n</div><span>' + this.closeChar + '</span>';
  }

  objToJsonView(obj: any, shallow?: boolean): string {
    return this.inspect(obj, shallow);
  }

  toJsonDiffView(changes: any, shallow?: boolean): string {
    const properties: string[] = [];

    if (changes.changed === 'equal') {
      return '';
    }

    const diff = changes.value;
    for (const key in diff) {
      if (diff[key].changed !== 'equal') {
        properties.push(this.formatChange(key, diff[key], shallow, true));
      }
    }

    return '<span>' + this.openChar + '</span>\n<div class="diff-level">' + properties.join('<span>,</span>\n') +
      '\n</div><span>' + this.closeChar + '</span>';
  }

  private equalObj(obj: any): any {
    return {
      changed: 'equal',
      value: obj
    };
  }

  private isValidAttr(a: any, b: any): boolean {
    const typeA = typeof a;
    const typeB = typeof b;
    return !!(a && b && (typeA === 'object' || typeA === 'function') && (typeB === 'object' || typeB === 'function'));
  }

  private formatChange(key: string, diffItem: any, shallow?: boolean, diffOnly?: boolean): string {
    const changed = diffItem.changed;
    switch (changed) {
      case 'equal':
        return this.stringifyObjectKey(this.escapeHTML(key)) + '<span>: </span>' + this.inspect(diffItem.value);
      case 'removed':
        return '<del class="diff">' + this.stringifyObjectKey(this.escapeHTML(key)) + '<span>: </span>' +
          this.inspect(diffItem.value) + '</del>';
      case 'added':
        return '<ins class="diff">' + this.stringifyObjectKey(this.escapeHTML(key)) + '<span>: </span>' +
          this.inspect(diffItem.value) + '</ins>';
      case 'primitive change': {
        const prefix = this.stringifyObjectKey(this.escapeHTML(key)) + '<span>: </span>';
        return '<del class="diff diff-key">' + prefix + this.inspect(diffItem.removed) + '</del><span>,</span>\n' +
          '<ins class="diff diff-key">' + prefix + this.inspect(diffItem.added) + '</ins>';
      }
      case 'object change':
        return shallow ? '' : (this.stringifyObjectKey(key) + '<span>: </span>' +
          (diffOnly ? this.toJsonDiffView(diffItem) : this.toJsonView(diffItem)));
      default:
        return '';
    }
  }

  private stringifyObjectKey(key: string): string {
    return /^[a-z0-9_$]*$/i.test(key) ? key : JSON.stringify(key);
  }

  private escapeHTML(string: string): string {
    return string.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  private inspect(obj: any, shallow?: boolean): string {
    return this.inspectAccum('', obj, shallow);
  }

  private inspectAccum(accumulator: string, obj: any, shallow?: boolean): string {
    switch (typeof obj) {
      case 'object':
        if (!obj) {
          return accumulator + 'null';
        }
        if (shallow) {
          return accumulator + '[object]';
        }
        {
          const keys = Object.keys(obj);
          const length = keys.length;
          if (length === 0) {
            return accumulator + '<span>' + this.openChar + this.closeChar + '</span>';
          }
          let result = accumulator + '<span>' + this.openChar + '</span>\n<div class="diff-level">';
          for (let i = 0; i < length; i++) {
            const key = keys[i];
            result = this.inspectAccum(result + this.stringifyObjectKey(this.escapeHTML(key)) + '<span>: </span>', obj[key]);
            if (i < length - 1) {
              result += '<span>,</span>\n';
            }
          }
          return result + '\n</div><span>' + this.closeChar + '</span>';
        }
      case 'string':
        return accumulator + JSON.stringify(this.escapeHTML(obj));
      case 'undefined':
        return accumulator + 'undefined';
      default:
        return accumulator + this.escapeHTML(String(obj));
    }
  }
}
